import {
  type ClassCode,
  type Event,
  type Invoice,
  type Payment,
  type PerDiem,
  type Quotable,
  type Reimbursement,
  type Task,
  type TimesheetLine,
  type Transaction,
  type TransactionAccount,
  type Trip,
  type User,
} from '@kalos/kalos-rpc';

import { type OrderDir } from '../../../tools/helpers';

export interface WeekClassCodeBreakdownSubtotal {
  weekStart: string;
  weekEnd: string;
  classCodeId: number;
  employeeId: number;
  hoursSubtotal: number;
}
/** Tabs whose rows can be sorted by their date column. */
export type DateSortTab = 'PerDiems' | 'Timesheets' | 'Trips' | 'Spiffs' | 'Reimbursements';

export type State = {
  loading: boolean;
  loadingEvent: boolean;
  perDiems: PerDiem[];
  timesheets: TimesheetLine[];
  transactions: Transaction[];
  costCenterTotals: { [key: string]: number };
  laborTotals: { [key: string]: number };
  classCodeDropdowns: { classCodeId: number; active: number }[];
  transactionAccounts: TransactionAccount[];
  totalHoursWorked: number;
  loadedInit: boolean;
  event: Event | undefined;
  loaded: boolean;
  trips: Trip[];
  tripsTotal: number;
  tasks: Task[];
  users: User[];
  classCodes: ClassCode[];
  transactionSort: { sortBy: string; sortDir: OrderDir };
  transactionDropDowns: { costCenterId: number; active: number }[];
  timesheetWeeklySubtotals: WeekClassCodeBreakdownSubtotal[];
  dropDowns: { perDiemId: number; active: number }[];
  activeTab: string;
  costCenterDropDownActive: boolean;
  laborTotalsDropDownActive: boolean;
  employeeHoursDropDownActive: boolean;
  openGalleryId: number | undefined;
  openReconcileId: number | undefined;
  printStatus: 'idle' | 'loading' | 'loaded' | undefined;
  showEmployeeSummary: boolean;
  invoice: Invoice | undefined;
  materials: Quotable[];
  payments: Payment[];
  reimbursements: Reimbursement[];
  dateSort: Record<DateSortTab, OrderDir>;
};

export enum ACTIONS {
  SET_LOADED = 'setLoaded',
  SET_LOADING_EVENT = 'setLoadingEvent',
  SET_PER_DIEMS = 'setPerDiems',
  SET_OPEN_GALLERY_ID = 'setOpenGalleryId',
  SET_OPEN_RECONCILE_ID = 'setOpenReconcileId',
  SET_TIMESHEETS = 'setTimesheets',
  SET_TRANSACTIONS = 'setTransactions',
  SET_USERS = 'setUsers',
  SET_TOTAL_HOURS_WORKED = 'setTotalHoursWorked',
  SET_LOADED_INIT = 'setLoadedInit',
  SET_EVENT = 'setEvent',
  SET_CLASS_CODES = 'setClassCodes',
  SET_COST_CENTER_TOTALS = 'setCostCenterTotals',
  SET_TRANSACTION_SORT = 'setTransactionSort',
  SET_LOADING = 'setLoading',
  SET_TRIPS = 'setTrips',
  SET_TRIPS_TOTAL = 'setTripsTotal',
  SET_TASKS = 'setTasks',
  SET_DROPDOWNS = 'setDropDowns',
  SET_CLASS_CODE_DROPDOWNS = 'setClassCodeDropdowns',
  SET_TRANSACTION_DROPDOWNS = 'setTransactionDropDowns',
  SET_TIMESHEET_WEEKLY_SUBTOTALS = 'setTimesheetWeeklySubtotals',
  SET_COST_CENTER_DROPDOWN_ACTIVE = 'setCostCenterDropdownActive',
  SET_LABOR_TOTALS_DROPDOWN_ACTIVE = 'setLaborTotalsDropdownActive',
  SET_EMPLOYEE_HOURS_DROPDOWN_ACTIVE = 'setEmployeeHoursDropdownActive',
  SET_LABOR_TOTALS = 'setLaborTotals',
  SET_ACTIVE_TAB = 'setActiveTab',
  SET_PRINT_STATUS = 'setPrintStatus',
  SET_TRANSACTION_ACCOUNTS = 'setTransactionAccounts',
  SET_ALL_DATA = 'setAllData',
  SET_SHOW_EMPLOYEE_SUMMARY = 'SET_SHOW_EMPLOYEE_SUMMARY',
  SET_INVOICE = 'setInvoice',
  SET_MATERIALS = 'setMaterials',
  SET_PAYMENTS = 'setPayments',
  SET_REIMBURSEMENTS = 'setReimbursements',
  SET_DATE_SORT = 'setDateSort',
}

export type Action =
  | { type: ACTIONS.SET_LOADED; data: boolean }
  | { type: ACTIONS.SET_LOADING; data: boolean }
  | { type: ACTIONS.SET_LOADING_EVENT; data: boolean }
  | { type: ACTIONS.SET_CLASS_CODES; data: ClassCode[] }
  | { type: ACTIONS.SET_PER_DIEMS; data: PerDiem[] }
  | { type: ACTIONS.SET_TIMESHEETS; data: TimesheetLine[] }
  | { type: ACTIONS.SET_TRANSACTIONS; data: Transaction[] }
  | { type: ACTIONS.SET_OPEN_GALLERY_ID; data: number | undefined }
  | { type: ACTIONS.SET_OPEN_RECONCILE_ID; data: number | undefined }
  | {
      type: ACTIONS.SET_CLASS_CODE_DROPDOWNS;
      data: { classCodeId: number; active: number }[];
    }
  | {
      type: ACTIONS.SET_ALL_DATA;
      data: {
        perDiemRes: PerDiem[];
        transactionRes: Transaction[];
        taskRes: Task[];
        tripRes: Trip[];
        timesheetRes: TimesheetLine[];
      };
    }
  | { type: ACTIONS.SET_COST_CENTER_TOTALS; data: { [key: string]: number } }
  | { type: ACTIONS.SET_LABOR_TOTALS; data: { [key: string]: number } }
  | { type: ACTIONS.SET_TOTAL_HOURS_WORKED; data: number }
  | { type: ACTIONS.SET_LOADED_INIT; data: boolean }
  | { type: ACTIONS.SET_COST_CENTER_DROPDOWN_ACTIVE; data: boolean }
  | { type: ACTIONS.SET_LABOR_TOTALS_DROPDOWN_ACTIVE; data: boolean }
  | { type: ACTIONS.SET_EMPLOYEE_HOURS_DROPDOWN_ACTIVE; data: boolean }
  | { type: ACTIONS.SET_EVENT; data: Event }
  | { type: ACTIONS.SET_USERS; data: User[] }
  | { type: ACTIONS.SET_TRIPS; data: Trip[] }
  | { type: ACTIONS.SET_TRANSACTION_ACCOUNTS; data: TransactionAccount[] }
  | { type: ACTIONS.SET_TRIPS_TOTAL; data: number }
  | {
      type: ACTIONS.SET_PRINT_STATUS;
      data: 'idle' | 'loading' | 'loaded' | undefined;
    }
  | { type: ACTIONS.SET_TASKS; data: Task[] }
  | {
      type: ACTIONS.SET_TRANSACTION_SORT;
      data: { sortBy: string; sortDir: OrderDir };
    }
  | { type: ACTIONS.SET_ACTIVE_TAB; data: string }
  | {
      type: ACTIONS.SET_DROPDOWNS;
      data: { perDiemId: number; active: number }[];
    }
  | {
      type: ACTIONS.SET_TIMESHEET_WEEKLY_SUBTOTALS;
      data: WeekClassCodeBreakdownSubtotal[];
    }
  | {
      type: ACTIONS.SET_TRANSACTION_DROPDOWNS;
      data: { costCenterId: number; active: number }[];
    }
  | { type: ACTIONS.SET_SHOW_EMPLOYEE_SUMMARY; data: boolean }
  | { type: ACTIONS.SET_INVOICE; data: Invoice | undefined }
  | { type: ACTIONS.SET_MATERIALS; data: Quotable[] }
  | { type: ACTIONS.SET_PAYMENTS; data: Payment[] }
  | { type: ACTIONS.SET_REIMBURSEMENTS; data: Reimbursement[] }
  | { type: ACTIONS.SET_DATE_SORT; data: { tab: DateSortTab; dir: OrderDir } };

export const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case ACTIONS.SET_LOADED: {
      return {
        ...state,
        loaded: action.data,
      };
    }
    case ACTIONS.SET_LOADING: {
      return {
        ...state,
        loading: action.data,
      };
    }
    case ACTIONS.SET_LOADING_EVENT: {
      return {
        ...state,
        loadingEvent: action.data,
      };
    }
    case ACTIONS.SET_CLASS_CODES: {
      return {
        ...state,
        classCodes: action.data,
      };
    }
    case ACTIONS.SET_PER_DIEMS: {
      return {
        ...state,
        perDiems: action.data,
      };
    }
    case ACTIONS.SET_TIMESHEETS: {
      return {
        ...state,
        timesheets: action.data,
      };
    }
    case ACTIONS.SET_TIMESHEET_WEEKLY_SUBTOTALS: {
      return {
        ...state,
        timesheetWeeklySubtotals: action.data,
      };
    }
    case ACTIONS.SET_TRANSACTIONS: {
      return {
        ...state,
        transactions: action.data,
      };
    }
    case ACTIONS.SET_USERS: {
      return {
        ...state,
        users: action.data,
      };
    }
    case ACTIONS.SET_ALL_DATA: {
      console.log('what we got in reducer', action.data);
      return {
        ...state,
        timesheets: action.data.timesheetRes,
        transactions: action.data.transactionRes,
        tasks: action.data.taskRes,
        perDiems: action.data.perDiemRes,
        trips: action.data.tripRes,
      };
    }
    case ACTIONS.SET_COST_CENTER_TOTALS: {
      return {
        ...state,
        costCenterTotals: action.data,
      };
    }
    case ACTIONS.SET_CLASS_CODE_DROPDOWNS: {
      return {
        ...state,
        classCodeDropdowns: action.data,
      };
    }
    case ACTIONS.SET_LABOR_TOTALS: {
      console.log('we set the labor total', action.data);
      return {
        ...state,
        setLaborTotals: action.data,
      };
    }
    case ACTIONS.SET_TRANSACTION_SORT: {
      console.log('we set the transaction sort', action.data);
      return {
        ...state,
        transactionSort: action.data,
      };
    }
    case ACTIONS.SET_TRANSACTION_ACCOUNTS: {
      console.log(action.data);
      return {
        ...state,
        transactionAccounts: action.data,
      };
    }
    case ACTIONS.SET_TOTAL_HOURS_WORKED: {
      return {
        ...state,
        totalHoursWorked: action.data,
      };
    }
    case ACTIONS.SET_LOADED_INIT: {
      return {
        ...state,
        loadedInit: action.data,
      };
    }
    case ACTIONS.SET_EVENT: {
      return {
        ...state,
        event: action.data,
      };
    }
    case ACTIONS.SET_TRIPS: {
      return {
        ...state,
        trips: action.data,
      };
    }
    case ACTIONS.SET_TRIPS_TOTAL: {
      return {
        ...state,
        tripsTotal: action.data,
      };
    }
    case ACTIONS.SET_TASKS: {
      return {
        ...state,
        tasks: action.data,
      };
    }
    case ACTIONS.SET_DROPDOWNS: {
      return {
        ...state,
        dropDowns: action.data,
      };
    }
    case ACTIONS.SET_COST_CENTER_DROPDOWN_ACTIVE: {
      return {
        ...state,
        costCenterDropDownActive: action.data,
      };
    }
    case ACTIONS.SET_LABOR_TOTALS_DROPDOWN_ACTIVE: {
      return {
        ...state,
        laborTotalsDropDownActive: action.data,
      };
    }
    case ACTIONS.SET_EMPLOYEE_HOURS_DROPDOWN_ACTIVE: {
      return {
        ...state,
        employeeHoursDropDownActive: action.data,
      };
    }
    case ACTIONS.SET_ACTIVE_TAB: {
      return {
        ...state,
        activeTab: action.data,
      };
    }
    case ACTIONS.SET_PRINT_STATUS: {
      return {
        ...state,
        printStatus: action.data,
      };
    }
    case ACTIONS.SET_SHOW_EMPLOYEE_SUMMARY: {
      return {
        ...state,
        showEmployeeSummary: action.data,
      };
    }
    case ACTIONS.SET_OPEN_GALLERY_ID: {
      return {
        ...state,
        openGalleryId: action.data,
      };
    }
    case ACTIONS.SET_OPEN_RECONCILE_ID: {
      return {
        ...state,
        openReconcileId: action.data,
      };
    }
    case ACTIONS.SET_INVOICE: {
      return {
        ...state,
        invoice: action.data,
      };
    }
    case ACTIONS.SET_MATERIALS: {
      return {
        ...state,
        materials: action.data,
      };
    }
    case ACTIONS.SET_PAYMENTS: {
      return {
        ...state,
        payments: action.data,
      };
    }
    case ACTIONS.SET_REIMBURSEMENTS: {
      return {
        ...state,
        reimbursements: action.data,
      };
    }
    case ACTIONS.SET_DATE_SORT: {
      return {
        ...state,
        dateSort: { ...state.dateSort, [action.data.tab]: action.data.dir },
      };
    }
    default:
      return state;
  }
};
